import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Repeat, Loader2, GraduationCap, Users } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Floating "Demo Mode" switcher, only rendered for Labs demo accounts.
 * Lets an investor / facilitator flip between the Parent and Student
 * experiences without logging out.
 */
export default function DemoSwitcher() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  if (!user || user.account_type !== 'lab') return null;
  // Showcase profiles have their own switcher, don't stack two demo bars
  if (user.showcase_profile || user.showcase_viewer) return null;

  const role = (user.role || 'student').toLowerCase();
  const isParent = role === 'parent';
  const targetLabel = isParent ? 'Student' : 'Parent';

  const switchView = async () => {
    setLoading(true);
    const token = sessionStorage.getItem('futurepath_token') || localStorage.getItem('futurepath_token');
    const usesSession = !!sessionStorage.getItem('futurepath_token');
    try {
      const { data } = await axios.post(`${API}/labs/pair`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const store = usesSession ? sessionStorage : localStorage;
      store.setItem('futurepath_token', data.access_token);
      toast.success(`Switched to ${data.role} view`);
      window.location.href = data.redirect || '/app/dashboard';
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't switch views.");
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] flex items-center gap-3 px-4 py-2 rounded-full bg-slate-900/90 backdrop-blur-md border border-cyan-400/30 shadow-lg shadow-blue-900/40"
      data-testid="demo-switcher"
    >
      <span className="flex items-center gap-1.5 text-xs font-semibold text-cyan-200">
        {isParent ? <Users className="w-3.5 h-3.5" /> : <GraduationCap className="w-3.5 h-3.5" />}
        Demo · {isParent ? 'Parent' : 'Student'} view
      </span>
      <button
        onClick={switchView}
        disabled={loading}
        data-testid="demo-switch-btn"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-xs font-semibold active:scale-95 transition disabled:opacity-60"
      >
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Repeat className="w-3.5 h-3.5" />}
        View as {targetLabel}
      </button>
    </div>
  );
}
